/**
 * Utility functions for handling image URL matching and fallbacks
 */

/**
 * Generates a cover art URL based on partial filename matching
 * This allows tracks with similar names (like remixes) to share the same cover art
 *
 * @param audioUrl - The audio file URL (e.g., "acetates/artist/song-remix-3.mp3")
 * @param existingCoverArt - Any explicitly defined cover art URL
 * @returns The cover art URL or undefined if no match found
 */
export function generateCoverArtUrl(audioUrl: string, existingCoverArt?: string): string | undefined {
  // If cover art is explicitly defined, use it
  if (existingCoverArt) {
    return existingCoverArt;
  }

  // Extract the base filename without extension
  const urlWithoutExtension = audioUrl.replace(/\.(mp3|wav|m4a|flac)$/i, '');

  // Split into path and filename
  const parts = urlWithoutExtension.split('/');
  const filename = parts[parts.length - 1];
  const pathPrefix = parts.slice(0, -1).join('/');

  // Strategy 1: Remove everything after known suffix keywords
  const baseFilename = filename
    .replace(/-(remix|alt|version|take|instrumental|acapella|radio-edit|extended|demo|live|acoustic|remaster|remastered|final|draft|edit|mix|dub|vocal|backing|karaoke).*$/i, '');

  // Don't apply strategy 2 - it's too aggressive and breaks normal track names
  // Just use the result from strategy 1

  const baseName = pathPrefix ? `${pathPrefix}/${baseFilename}` : baseFilename;

  // Generate the corresponding image URL
  return `${baseName}.png`;
}

/**
 * Extracts the artist/album folder from an audio URL
 * @param audioUrl - The audio file URL
 * @returns The folder path (e.g., "acetates/ginger-and-the-chiffons")
 */
export function extractArtistFolder(audioUrl: string): string {
  const parts = audioUrl.split('/');
  if (parts.length >= 2) {
    return parts.slice(0, -1).join('/');
  }
  return '';
}

/**
 * Generates all possible image variants for a track
 * Returns an array of potential image URLs in order of preference
 * @param audioUrl - The audio file URL
 * @param existingCoverArt - Any explicitly defined cover art URL
 * @returns Array of potential image URLs
 */
export function generateImageVariants(audioUrl: string, existingCoverArt?: string): string[] {
  const variants: string[] = [];

  // First priority: explicit cover art
  if (existingCoverArt) {
    variants.push(existingCoverArt);
  }

  // Extract base components
  const urlWithoutExtension = audioUrl.replace(/\.(mp3|wav|m4a|flac)$/i, '');
  const parts = urlWithoutExtension.split('/');
  const filename = parts[parts.length - 1];
  const pathPrefix = parts.slice(0, -1).join('/');

  // Second priority: exact filename match with .png
  const exactMatch = `${urlWithoutExtension}.png`;
  if (!variants.includes(exactMatch)) {
    variants.push(exactMatch);
  }

  // Third priority: remove known suffix patterns
  const withoutKnownSuffix = filename
    .replace(/-(remix|alt|version|take|instrumental|acapella|radio-edit|extended|demo|live|acoustic|remaster|remastered|final|draft|edit|mix|dub|vocal|backing|karaoke).*$/i, '');

  if (withoutKnownSuffix !== filename) {
    const knownSuffixPath = pathPrefix ? `${pathPrefix}/${withoutKnownSuffix}.png` : `${withoutKnownSuffix}.png`;
    if (!variants.includes(knownSuffixPath)) {
      variants.push(knownSuffixPath);
    }
  }

  // Fourth priority: progressively remove hyphenated segments from the end
  // This generates multiple fallbacks like: song-name-xyz -> song-name -> song
  const segments = filename.split('-');
  for (let i = segments.length - 1; i > 0; i--) {
    const truncated = segments.slice(0, i).join('-');
    const truncatedPath = pathPrefix ? `${pathPrefix}/${truncated}.png` : `${truncated}.png`;
    if (!variants.includes(truncatedPath)) {
      variants.push(truncatedPath);
    }
  }

  return variants;
}

/**
 * Checks if an image exists (for future use with actual HTTP checks)
 * Currently returns true for demo purposes
 * @param imageUrl - The image URL to check
 * @returns Promise resolving to boolean indicating if image exists
 */
export async function imageExists(imageUrl: string): Promise<boolean> {
  try {
    const response = await fetch(imageUrl, { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Gets the best available image URL for a track
 * @param audioUrl - The audio file URL
 * @param existingCoverArt - Any explicitly defined cover art URL
 * @returns The best available image URL or undefined
 */
export function getBestImageUrl(audioUrl: string, existingCoverArt?: string): string | undefined {
  const variants = generateImageVariants(audioUrl, existingCoverArt);
  // For now, return the first variant (most preferred)
  // In the future, this could check which images actually exist
  return variants[0];
}